import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Home,
  User,
  Ticket,
  LayoutDashboard,
  LogOut,
  Menu,
  Sparkles,
} from "lucide-react";
import { LogoutUserFunc } from "../../redux/actions/User";
import { useProfilePage } from "./ProfileSidebarContext";

const SECTION_LABELS = {
  2: "Account Details",
  4: "Organizer Dashboard",
};

function itemClass(active) {
  return `flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] transition-all w-full border ${
    active
      ? "text-white bg-red-600/10 border-red-600/20"
      : "text-white/60 hover:text-white hover:bg-white/[0.05] border-transparent"
  }`;
}

export default function ProfileMobileNavDropdown() {
  const { active, setActive } = useProfilePage();
  const user = useSelector((state) => state.user.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    setOpen(false);
  }, [active]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <div ref={rootRef} className="relative lg:hidden">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={open ? "Close profile menu" : "Open profile menu"}
        className="p-2 text-white/60 hover:text-white"
      >
        <Menu className="w-5 h-5" strokeWidth={2} />
      </button>

      {open && (
        <div
          className="absolute top-full right-0 z-[150] mt-2 w-[min(calc(100vw-24px),280px)] max-h-[min(70vh,420px)] overflow-y-auto rounded-xl border border-red-600/20 bg-[#0d1117] shadow-2xl py-2"
          role="listbox"
          style={{ fontFamily: "'Outfit', 'Inter', sans-serif" }}
        >
          <div className="px-2 py-1.5">
            <span
              className="px-2 text-[9px] text-white/30 uppercase tracking-[0.18em]"
              style={{ fontWeight: 700 }}
            >
              My Profile
            </span>
          </div>

          <div className="px-2 space-y-0.5">
            <Link to="/" onClick={close} className={itemClass(false)}>
              <Home className="w-4 h-4 shrink-0" strokeWidth={1.8} />
              Home
            </Link>

            <button
              type="button"
              onClick={() => {
                setActive(2);
                close();
              }}
              className={itemClass(active === 2)}
            >
              <User className="w-4 h-4 shrink-0" strokeWidth={1.8} />
              {SECTION_LABELS[2]}
            </button>

            <Link to="/my-tickets" onClick={close} className={itemClass(false)}>
              <Ticket className="w-4 h-4 shrink-0" strokeWidth={1.8} />
              My Tickets
            </Link>

            <Link
              to={user?.capabilities?.creator && user.capabilities.creator !== "REJECTED" ? "/creators/dashboard" : "/creators"}
              onClick={close}
              className={itemClass(false)}
            >
              <Sparkles className="w-4 h-4 shrink-0" strokeWidth={1.8} />
              {user?.capabilities?.creator && user.capabilities.creator !== "REJECTED" ? "Creator dashboard" : "Promote & earn"}
            </Link>

            {user?.role === "admin" && (
              <Link to="/admin/dashboard" onClick={close} className={itemClass(false)}>
                <LayoutDashboard className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                Admin Dashboard
              </Link>
            )}

            {(user?.capabilities?.host || user?.userType === "organizer" || user?.role === "admin") && (
              <button
                type="button"
                onClick={() => {
                  setActive(4);
                  close();
                }}
                className={itemClass(active === 4)}
              >
                <LayoutDashboard className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                {SECTION_LABELS[4]}
              </button>
            )}
          </div>

          <div className="mt-1 px-2 pt-2 border-t border-red-600/10">
            <button
              type="button"
              onClick={() => {
                close();
                dispatch(LogoutUserFunc(navigate));
              }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[12px] text-white/40 hover:text-red-400 hover:bg-red-500/5 transition-all w-full border border-transparent"
            >
              <LogOut className="w-4 h-4 shrink-0" strokeWidth={1.8} />
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
